import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  Bot,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Layers,
  Loader2,
  Paperclip,
  Pencil,
  RefreshCw,
  Send,
  Sparkles,
  Square,
  User,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useChat } from './useChat';
import Markdown from './Markdown';
import ToolCallCard from './ToolCallCard';
import SecureFormBubble from './SecureFormBubble';
import AttachmentChip from './AttachmentChip';
import AttachmentViewer from './AttachmentViewer';
import { fetchAgentTask, type AgentTask } from '../../lib/api';
import type { ChatUiMessage } from './types';

interface ChatPanelProps {
  conversationId: string | null;
  template?: string;
  agentMode?: boolean;
  headerRight?: React.ReactNode;
  onConversationId?: (id: string) => void;
  onConversationChanged?: () => void;
  onAgentTasks?: (tasks: AgentTask[]) => void;
  clarificationTaskId?: string | null;
  onAnswerClarification?: (taskId: string, answer: string) => void;
}

const SUGGESTIONS = [
  {
    icon: Layers,
    title: 'Reconcile last month',
    prompt:
      'Reconcile last month: pull Mercury transactions, match them against open invoices, and list anything unmatched.',
  },
  {
    icon: Sparkles,
    title: 'What needs my attention?',
    prompt:
      'Summarize what needs my attention right now across invoices, receipts, sync failures, and upcoming tax deadlines.',
  },
  {
    icon: Paperclip,
    title: 'Sort my uploads',
    prompt:
      'Classify the files I just attached, tell me what each one is, and route them to the right folder in oCIS.',
  },
  {
    icon: RefreshCw,
    title: 'Estimate quarterly taxes',
    prompt:
      'Estimate my next quarterly tax payment using current-year revenue and expenses, and cite the IRS forms involved.',
  },
];

function AssistantBubble({
  message,
  onDecision,
  deciding,
  onOpenAttachment,
  isLast,
  onRegenerate,
  canRegenerate,
}: {
  message: ChatUiMessage;
  onDecision: ReturnType<typeof useChat>['decideApproval'];
  deciding: boolean;
  onOpenAttachment: (id: string) => void;
  isLast: boolean;
  onRegenerate: () => void;
  canRegenerate: boolean;
}) {
  const [thinkingOpen, setThinkingOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const empty =
    !message.content &&
    message.toolCalls.length === 0 &&
    !message.secureForms?.length &&
    !message.thinking;
  const showThinkingLoader = message.streaming && message.thinkingStreaming && !message.thinking;

  const handleCopy = () => {
    void navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="group flex gap-3">
      <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-zt-border bg-zt-elevated">
        <Bot className="text-zt-primary" size={15} />
      </div>
      <div className="min-w-0 flex-1 space-y-2.5">
        {message.agentRun && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-zt-border bg-zt-elevated/50 px-3 py-1.5 text-xs text-slate-300">
            <Layers size={13} className="text-zt-info" />
            <span className="font-medium text-slate-200">Agent run</span>
            <span className="capitalize text-zt-muted">{message.agentRun.status}</span>
            {typeof message.agentRun.subtaskCount === 'number' && (
              <span className="text-zt-muted">{message.agentRun.subtaskCount} subtasks</span>
            )}
            {message.agentRun.planSteps?.length ? (
              <span className="truncate text-zt-muted">
                {message.agentRun.planSteps.join(' → ')}
              </span>
            ) : null}
          </div>
        )}

        {(message.thinking || showThinkingLoader) && (
          <div className="rounded-md border border-zt-border bg-zt-bg/80">
            <button
              type="button"
              onClick={() => setThinkingOpen((o) => !o)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-zt-muted hover:text-slate-300"
            >
              {thinkingOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              Thinking
              {message.thinkingStreaming && (
                <Loader2 size={12} className="animate-spin text-zt-muted" />
              )}
            </button>
            {thinkingOpen && message.thinking && (
              <div className="whitespace-pre-wrap border-t border-zt-border/60 px-3 py-2.5 text-[12px] leading-relaxed text-slate-400">
                {message.thinking}
              </div>
            )}
            {thinkingOpen && !message.thinking && showThinkingLoader && (
              <div className="border-t border-zt-border/60 px-3 py-2.5 text-xs text-zt-muted">
                Working…
              </div>
            )}
          </div>
        )}

        {message.toolCalls.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-[10px] font-medium uppercase tracking-wider text-zt-muted">
              Tool calls
              <span className="ml-1.5 font-normal normal-case tracking-normal text-zt-muted/80">
                {message.toolCalls.filter((t) => t.status === 'done').length}/
                {message.toolCalls.length}
              </span>
            </div>
            {message.toolCalls.map((tc) => (
              <ToolCallCard key={tc.id} toolCall={tc} onDecision={onDecision} deciding={deciding} />
            ))}
          </div>
        )}

        {(message.secureForms ?? []).map((sf) => (
          <SecureFormBubble
            key={sf.id}
            formId={sf.formId}
            title={sf.title}
            fields={sf.fields}
            irsFormNumber={sf.irsFormNumber}
            fieldMapping={sf.fieldMapping}
            shareUrl={sf.shareUrl}
            recipientName={sf.recipientName}
            expiresAt={sf.expiresAt}
            purpose={sf.purpose}
            toolCallId={sf.toolCallId}
          />
        ))}

        {empty && message.streaming && !message.thinkingStreaming && (
          <div className="flex items-center gap-2 py-1 text-sm text-zt-muted">
            <Loader2 size={14} className="animate-spin" /> Working…
          </div>
        )}

        {message.content && (
          <div className="text-[15px] leading-7 text-slate-200">
            <Markdown content={message.content} />
          </div>
        )}
        {message.streaming && message.content && (
          <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-zt-primary align-middle" />
        )}
        {message.error && <div className="mt-1 text-sm text-zt-danger">{message.error}</div>}

        {!message.streaming && message.content && (
          <div
            className={`flex items-center gap-1 transition ${
              isLast ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            }`}
          >
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] text-zt-muted hover:bg-zt-elevated hover:text-slate-200"
            >
              {copied ? <Check size={12} className="text-zt-primary" /> : <Copy size={12} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            {isLast && canRegenerate && (
              <button
                type="button"
                onClick={onRegenerate}
                className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] text-zt-muted hover:bg-zt-elevated hover:text-slate-200"
              >
                <RefreshCw size={12} /> Regenerate
              </button>
            )}
          </div>
        )}

        {message.attachments?.length ? (
          <div className="flex flex-wrap gap-1.5">
            {message.attachments.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => onOpenAttachment(a.id)}
                title={[a.shortRef ? `ref ${a.shortRef}` : null, a.ocisPath, a.onServer ? 'on server' : null]
                  .filter(Boolean)
                  .join(' · ')}
                className="rounded border border-zt-border bg-zt-bg px-2 py-1 text-[11px] text-slate-300 hover:border-zt-primary hover:text-white"
              >
                {a.filename}
                {a.shortRef ? (
                  <span className="ml-1 font-mono text-[10px] text-zt-muted">#{a.shortRef}</span>
                ) : null}
                {a.onServer ? <span className="ml-1 text-[10px] text-zt-muted">server</span> : null}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function UserBubble({
  message,
  onOpenAttachment,
  onEdit,
  editable,
}: {
  message: ChatUiMessage;
  onOpenAttachment: (id: string) => void;
  onEdit: (id: string, text: string) => void;
  editable: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.content);

  useEffect(() => setDraft(message.content), [message.content]);

  return (
    <div className="group flex justify-end gap-3">
      <div className="flex max-w-[85%] flex-col items-end">
        {editing ? (
          <div className="w-full rounded-lg border border-zt-primary/40 bg-zt-bg p-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={Math.min(8, draft.split('\n').length + 1)}
              className="w-full resize-none bg-transparent text-sm text-slate-100 focus:outline-none"
            />
            <div className="mt-1 flex justify-end gap-1">
              <button
                type="button"
                onClick={() => {
                  setDraft(message.content);
                  setEditing(false);
                }}
                className="flex items-center gap-1 rounded px-2 py-1 text-[11px] text-zt-muted hover:text-slate-200"
              >
                <X size={12} /> Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  onEdit(message.id, draft);
                }}
                className="flex items-center gap-1 rounded bg-zt-primary px-2 py-1 text-[11px] font-medium text-zt-bg hover:bg-zt-accent"
              >
                <Send size={12} /> Send
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-zt-border bg-zt-elevated/60 px-4 py-2.5 text-[15px] leading-7 text-slate-100">
            <Markdown content={message.content} />
          </div>
        )}

        {message.attachments?.length ? (
          <div className="mt-1.5 flex flex-wrap justify-end gap-1.5">
            {message.attachments.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => onOpenAttachment(a.id)}
                title={[a.shortRef ? `ref ${a.shortRef}` : null, a.ocisPath, a.onServer ? 'on server' : null]
                  .filter(Boolean)
                  .join(' · ')}
                className="rounded border border-zt-border bg-zt-bg px-2 py-1 text-[11px] text-slate-300 hover:border-zt-primary hover:text-white"
              >
                <Paperclip size={10} className="mr-1 inline" />
                {a.filename}
                {a.shortRef ? (
                  <span className="ml-1 font-mono text-[10px] text-zt-muted">#{a.shortRef}</span>
                ) : null}
                {a.onServer ? <span className="ml-1 text-[10px] text-zt-muted">server</span> : null}
              </button>
            ))}
          </div>
        ) : null}

        {editable && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="mt-1 flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-zt-muted opacity-0 transition hover:text-slate-200 group-hover:opacity-100"
          >
            <Pencil size={11} /> Edit
          </button>
        )}
      </div>
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-zt-elevated">
        <User className="text-zt-muted" size={16} />
      </div>
    </div>
  );
}

function ChatPanel({
  conversationId,
  template,
  agentMode,
  headerRight,
  onConversationId,
  onConversationChanged,
  onAgentTasks,
  clarificationTaskId,
  onAnswerClarification,
}: ChatPanelProps) {
  const {
    messages,
    attachments,
    sending,
    loadingHistory,
    error,
    warning,
    canResume,
    send,
    stop,
    resume,
    regenerate,
    editAndResend,
    addAttachments,
    removeAttachment,
    decideApproval,
    reloadHistory,
    clearWarning,
    activeTaskId,
  } = useChat({
    template,
    agentMode,
    conversationId,
    onConversationId,
    onConversationChanged,
  });

  const [input, setInput] = useState('');
  const [dragging, setDragging] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [clarifyInput, setClarifyInput] = useState('');
  const [viewingAttachment, setViewingAttachment] = useState<string | null>(null);
  const [atBottom, setAtBottom] = useState(true);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const atBottomRef = useRef(true);
  const stickBottomRef = useRef(true);

  // Sticky-bottom: only follow new tokens when the operator is already at the
  // tail. Instant scrollTop assignment avoids smooth-scroll fighting mid-read.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !stickBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    atBottomRef.current = near;
    stickBottomRef.current = near;
    setAtBottom(near);
  }, []);

  useEffect(() => {
    if (!activeTaskId || !onAgentTasks) return;
    let cancelled = false;

    const poll = async () => {
      const res = await fetchAgentTask(activeTaskId);
      if (!cancelled && res.ok && res.data) {
        onAgentTasks([res.data, ...(res.data.children ?? [])]);
      }
    };

    void poll();
    // Subtasks land after the initial event, so keep polling while the run is live.
    const timer = sending ? window.setInterval(() => void poll(), 3000) : undefined;
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearInterval(timer);
    };
  }, [activeTaskId, onAgentTasks, sending]);

  // The panel persists across conversation switches so an in-flight stream is
  // never torn down; per-conversation view state still has to be reset.
  useEffect(() => {
    setInput('');
    setViewingAttachment(null);
    setClarifyInput('');
    setAtBottom(true);
    atBottomRef.current = true;
    stickBottomRef.current = true;
  }, [conversationId]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [input]);

  const handleSend = (text?: string) => {
    const value = text ?? input;
    if (!value.trim()) return;
    setInput('');
    setAtBottom(true);
    atBottomRef.current = true;
    stickBottomRef.current = true;
    void send(value);
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFiles = (files: FileList | File[] | null) => {
    const list = files ? Array.from(files) : [];
    if (list.length === 0) return;
    void addAttachments(list);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files);
    if (files.length > 0) {
      e.preventDefault();
      handleFiles(files);
      toast.success(`Attached ${files.length} pasted file${files.length > 1 ? 's' : ''}`);
    }
  };

  const handleDecision = async (
    ...args: Parameters<ReturnType<typeof useChat>['decideApproval']>
  ) => {
    setDeciding(true);
    try {
      await decideApproval(...args);
    } finally {
      setDeciding(false);
    }
  };

  const lastAssistantId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'assistant') return messages[i].id;
    }
    return null;
  }, [messages]);

  const uploading = attachments.some((a) => a.status === 'uploading');
  const canSend = input.trim().length > 0 && !sending && !uploading;

  return (
    <div
      className="relative flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-zt-border bg-zt-surface"
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        handleFiles(e.dataTransfer.files);
      }}
    >
      <div className="flex items-center gap-2.5 border-b border-zt-border px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-md border border-zt-border bg-zt-elevated">
          <Bot className="text-zt-primary" size={16} />
        </div>
        <div className="min-w-0">
          <div className="font-medium leading-tight text-white">ZeroTail Assistant</div>
          <div className="text-[11px] text-zt-muted">
            {agentMode ? 'Agent orchestrator' : 'Tool chat'}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">{headerRight}</div>
      </div>

      {clarificationTaskId && (
        <div className="border-b border-amber-500/30 bg-amber-500/10 px-4 py-3">
          <p className="text-sm text-amber-200">The agent needs your input to continue.</p>
          <div className="mt-2 flex gap-2">
            <input
              value={clarifyInput}
              onChange={(e) => setClarifyInput(e.target.value)}
              className="flex-1 rounded border border-zt-border bg-zt-bg px-2 py-1 text-sm text-slate-200"
              placeholder="Your answer…"
            />
            <button
              type="button"
              className="rounded bg-zt-primary px-3 py-1 text-sm text-zt-bg"
              onClick={() => {
                const tid = clarificationTaskId || activeTaskId;
                if (tid && clarifyInput.trim()) {
                  onAnswerClarification?.(tid, clarifyInput.trim());
                  setClarifyInput('');
                }
              }}
            >
              Submit
            </button>
          </div>
        </div>
      )}

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="relative min-h-0 flex-1 overflow-y-auto px-4 py-5"
      >
        <div
          className={[
            'mx-auto flex min-h-full max-w-3xl flex-col space-y-6',
            messages.length > 0 ? 'justify-end' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {loadingHistory && (
            <div className="flex items-center gap-2 text-sm text-zt-muted">
              <Loader2 size={14} className="animate-spin" /> Loading conversation…
            </div>
          )}

          {!loadingHistory && messages.length === 0 && (
            <div className="pt-6 sm:pt-10">
              <div className="mb-6 text-center">
                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-md border border-zt-border bg-zt-elevated">
                  <Bot className="text-zt-primary" size={20} />
                </div>
                <h3 className="text-lg font-semibold text-white sm:text-xl">
                  ZeroTail Assistant
                </h3>
                <p className="mx-auto mt-1.5 max-w-lg text-sm leading-relaxed text-zt-muted">
                  Query the books, draft invoices, reconcile payments, OCR receipts, and stage
                  sensitive actions for approval. Attach files or pick a starter below.
                </p>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s.title}
                    type="button"
                    onClick={() => handleSend(s.prompt)}
                    className="rounded-md border border-zt-border bg-zt-bg p-3 text-left transition hover:border-zt-primary/40 hover:bg-zt-elevated"
                  >
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-200">
                      <s.icon size={14} className="text-zt-primary" />
                      {s.title}
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-zt-muted">
                      {s.prompt}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg) =>
            msg.role === 'user' ? (
              <UserBubble
                key={msg.id}
                message={msg}
                onOpenAttachment={setViewingAttachment}
                onEdit={(id, text) => void editAndResend(id, text)}
                editable={!sending}
              />
            ) : (
              <AssistantBubble
                key={msg.id}
                message={msg}
                onDecision={handleDecision}
                deciding={deciding}
                onOpenAttachment={setViewingAttachment}
                isLast={msg.id === lastAssistantId}
                onRegenerate={() => void regenerate()}
                canRegenerate={!sending}
              />
            )
          )}
          <div ref={bottomRef} />
        </div>

        {!atBottom && (
          <button
            type="button"
            onClick={() => {
              setAtBottom(true);
              atBottomRef.current = true;
              stickBottomRef.current = true;
              const el = scrollRef.current;
              if (el) el.scrollTop = el.scrollHeight;
            }}
            className="sticky bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full border border-zt-border bg-zt-elevated px-3 py-1.5 text-xs text-slate-200 shadow-lg hover:border-zt-primary"
          >
            <ArrowDown size={13} /> Jump to latest
          </button>
        )}
      </div>

      {dragging && (
        <div className="pointer-events-none absolute inset-3 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-zt-primary/60 bg-zt-bg/80 text-sm text-zt-primary">
          <Paperclip size={16} className="mr-2" /> Drop files to attach
        </div>
      )}

      {warning && (
        <div className="flex items-center gap-2 border-t border-zt-border bg-zt-warning/5 px-4 py-2 text-xs text-zt-warning">
          <span className="flex-1">{warning}</span>
          <button
            type="button"
            onClick={clearWarning}
            className="rounded border border-zt-border bg-zt-bg px-2 py-1 text-[11px] font-medium text-slate-200 hover:border-zt-primary hover:text-white"
          >
            Dismiss
          </button>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 border-t border-zt-border bg-zt-danger/5 px-4 py-2 text-xs text-zt-danger">
          <span className="flex-1">{error}</span>
          {!sending && !canResume && conversationId && /could not load/i.test(error) && (
            <button
              type="button"
              onClick={() => reloadHistory()}
              className="rounded border border-zt-border bg-zt-bg px-2 py-1 text-[11px] font-medium text-slate-200 hover:border-zt-primary hover:text-white"
            >
              Reload
            </button>
          )}
          {canResume && !sending && (
            <button
              type="button"
              onClick={() => void resume()}
              className="rounded border border-zt-border bg-zt-bg px-2 py-1 text-[11px] font-medium text-slate-200 hover:border-zt-primary hover:text-white"
            >
              Resume
            </button>
          )}
        </div>
      )}

      <div className="border-t border-zt-border p-4">
        {attachments.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {attachments.map((att) => (
              <AttachmentChip
                key={att.localId}
                attachment={att}
                onRemove={removeAttachment}
                onOpen={setViewingAttachment}
              />
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-zt-border bg-zt-bg text-zt-muted transition hover:border-zt-primary hover:text-zt-primary"
            aria-label="Attach files"
            title="Attach files"
          >
            <Paperclip size={16} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            rows={1}
            placeholder="Ask anything, or paste a screenshot — Enter to send, Shift+Enter for a new line"
            className="max-h-52 flex-1 resize-none rounded-lg border border-zt-border bg-zt-bg px-3 py-2.5 text-sm text-slate-200 placeholder-zt-muted focus:border-zt-primary focus:outline-none"
          />
          {sending ? (
            <button
              type="button"
              onClick={stop}
              className="flex h-10 shrink-0 items-center gap-1 rounded-lg border border-zt-border bg-zt-bg px-3 text-sm font-medium text-slate-200 hover:border-zt-danger hover:text-zt-danger"
            >
              <Square size={14} /> Stop
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleSend()}
              disabled={!canSend}
              className="flex h-10 shrink-0 items-center gap-1 rounded-lg bg-zt-primary px-4 text-sm font-medium text-zt-bg transition hover:bg-zt-accent disabled:opacity-40"
            >
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              {uploading ? 'Uploading' : 'Send'}
            </button>
          )}
        </div>
      </div>

      <AttachmentViewer
        attachmentId={viewingAttachment}
        onClose={() => setViewingAttachment(null)}
      />
    </div>
  );
}

export default ChatPanel;
